import { TaskManagementActions } from './TaskManagementActions';
import { TaskRelatedRecords } from './TaskRelatedRecords';
import { TaskWorkspace } from './TaskWorkspace';
import React, { useState } from 'react';
import {
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  Stack,
  Chip,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Grid,
} from '@mui/joy';
import {
  PencilSimple as EditIcon,
  CheckCircle as CheckCircleIcon,
  FloppyDisk as SaveIcon,
} from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import type { Task } from '../../types/tasks';
import { TaskEditModal } from './TaskEditModal';
import { TaskComments } from './TaskComments';
import { useTaskComments } from '../../hooks/useComments';
import { useAuth } from '../../contexts/AuthContext';

interface TaskDetailModalProps {
  open: boolean;
  task: Task;
  onClose: () => void;
  onUpdate: () => void;
  onTaskUpdated?: (updatedTask: Task) => void;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  open,
  task,
  onClose,
  onUpdate,
  onTaskUpdated,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(task);
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  
  // Fetch comments count for tab badge
  const { data: comments = [], count: commentCount } = useTaskComments(task.id, open);

  // Fetch task details to ensure we have the latest data
  const { data: taskDetails, refetch: refetchTask, isError: taskError, isFetching: taskFetching } = useQuery({
    queryKey: ['task', task.id],
    queryFn: async () => {
      const response = await apiClient.get<Task>(`/api/tasks/${task.id}/`);
      return response.data;
    },
    enabled: open && !!task.id,

  });

  // Update currentTask when task details are fetched
  React.useEffect(() => {
    if (taskDetails) {
      setCurrentTask(taskDetails);
    }
  }, [taskDetails]);

  // Update currentTask when task prop changes
  React.useEffect(() => {
    setCurrentTask(task);
  }, [task]);

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/api/tasks/${currentTask.id}/complete/`, {});
      return response.data;
    },
    onSuccess: async (updatedTask) => {
      // Refetch to get complete task data
      await refetchTask();
      
      // Invalidate job tasks to update the Tasks tab
      if (currentTask.related_job) {
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['jobTasks', String(currentTask.related_job)] });
        // Also invalidate the tasks query used by useTasksQuery
        queryClient.invalidateQueries({ queryKey: ['tasks', currentTask.related_job] });
        queryClient.invalidateQueries({ queryKey: ['tasks', String(currentTask.related_job)] });
      }
      
      // Invalidate opportunity tasks if applicable
      if (currentTask.related_opportunity) {
        queryClient.invalidateQueries({ queryKey: ['opportunityTasks', String(currentTask.related_opportunity)] });
        queryClient.invalidateQueries({ queryKey: ['tasks', { opportunity_id: currentTask.related_opportunity }] });
      }
      
      onTaskUpdated?.(updatedTask as Task);
      onUpdate();
    },
  });

  // Start task mutation
  const startTaskMutation = useMutation({
    mutationFn: async (): Promise<Task> => {
      const response = await apiClient.post(`/api/tasks/${currentTask.id}/start/`, {});
      return response.data as Task;
    },
    onSuccess: async (updatedTask: Task) => {
      // Refetch to get complete task data
      await refetchTask();
      onTaskUpdated?.(updatedTask as Task);
      onUpdate();
    },
  });

  // Promote a task to a reusable template, so the next "Order material" or
  // "Final walkthrough" is picked from a list instead of retyped from memory.
  const saveAsTemplateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post('/api/task-templates/', {
        name: currentTask.name,
        description: currentTask.description || '',
        instructions: currentTask.instructions || '',
        task_type: currentTask.task_type,
        priority: currentTask.priority,
        estimated_hours: currentTask.estimated_hours ?? null,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-templates'] });
    },
  });

  const priorityColors = {
    low: 'neutral',
    medium: 'primary',
    high: 'warning',
    critical: 'danger',
  } as const;

  const statusColors = {
    not_started: 'neutral',
    in_progress: 'primary',
    on_hold: 'warning',
    completed: 'success',
    cancelled: 'danger',
  } as const;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog size="lg" sx={{ overflow: 'auto', maxWidth: 1200, width: '90vw' }}>
        <ModalClose aria-label="Close task" />
        
        {taskError && <Alert color="danger">This task is unavailable or could not be loaded. <Button onClick={() => refetchTask()}>Retry</Button></Alert>}
        {(completeTaskMutation.isError || startTaskMutation.isError || saveAsTemplateMutation.isError) && <Alert color="danger">{String((completeTaskMutation.error || startTaskMutation.error || saveAsTemplateMutation.error as any)?.message || 'The task could not be updated. Check prerequisites and retry.')}</Alert>}
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography level="h3" sx={{ mb: 1 }}>
                {currentTask.name}
              </Typography>
              
              <Stack direction="row" spacing={1} alignItems="center">
                {currentTask.is_archived && <Chip size="sm" color="neutral">Archived</Chip>}
                <Chip
                  size="sm"
                  variant="soft"
                  color={priorityColors[currentTask.priority]}
                >
                  {currentTask.priority_display}
                </Chip>
                
                <Chip
                  size="sm"
                  variant="soft"
                  color={statusColors[currentTask.status]}
                >
                  {currentTask.status_display}
                </Chip>
                
                {currentTask.is_overdue && (
                  <Chip size="sm" variant="solid" color="danger">
                    Overdue
                  </Chip>
                )}
              </Stack>
            </Box>
            
            <Stack direction="row" spacing={1} sx={{ mr: 4, flexWrap: 'wrap', gap: 1 }}>
              {currentTask.status !== 'completed' && (
                <Button
                  size="sm"
                  startDecorator={<CheckCircleIcon />}
                  onClick={() => completeTaskMutation.mutate()}
                  loading={completeTaskMutation.isPending}
                  disabled={taskError || taskFetching || currentTask.is_archived}
                >
                  Complete
                </Button>
              )}

              <Button
                size="sm"
                variant="outlined"
                color={saveAsTemplateMutation.isSuccess ? 'success' : 'neutral'}
                startDecorator={<SaveIcon />}
                onClick={() => saveAsTemplateMutation.mutate()}
                loading={saveAsTemplateMutation.isPending}
                disabled={saveAsTemplateMutation.isSuccess}
              >
                {saveAsTemplateMutation.isSuccess ? 'Saved as Template' : 'Save as Template'}
              </Button>

              <IconButton 
                size="sm" 
                variant="outlined"
                onClick={() => setIsEditModalOpen(true)}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        {/* Progress Bar */}
        {currentTask.completion_percentage > 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Typography level="body-sm">Progress</Typography>
              <Typography level="body-xs" color="neutral">
                {currentTask.completion_percentage}%
              </Typography>
            </Stack>
            <LinearProgress
              determinate
              value={currentTask.completion_percentage}
              color={currentTask.completion_percentage === 100 ? 'success' : 'primary'}
            />
          </Box>
        )}

        {/* Main Content */}
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
          <TabList sx={{ flexWrap: 'wrap' }}>
            <Tab>Details</Tab>
            <Tab>
              Comments & Activity
              {comments.length > 0 && (
                <Chip size="sm" variant="soft" sx={{ ml: 1 }}>
                  {commentCount ?? comments.length}
                </Chip>
              )}
            </Tab>
            <Tab>Instructions</Tab><Tab>Checklist & Files</Tab>
          </TabList>

          <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
            <Box sx={{ mb: 2 }}><TaskRelatedRecords task={currentTask} onNavigate={onClose} /></Box>
            <Grid container spacing={3}>
              {/* Left Column - Description */}
              <Grid xs={12} md={7}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography level="title-sm" sx={{ mb: 2 }}>
                      Description
                    </Typography>
                    
                    {currentTask.description ? (
                      <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                        {currentTask.description}
                      </Typography>
                    ) : (
                      <Typography 
                        level="body-sm" 
                        sx={{ 
                          color: 'text.tertiary',
                          fontStyle: 'italic' 
                        }}
                      >
                        No description provided
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Right Column - Task Details */}
              <Grid xs={12} md={5}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-sm" sx={{ mb: 2 }}>
                      Details
                    </Typography>
                    
                    <Stack spacing={2}>
                      <Box>
                        <Typography level="body-sm" fontWeight="md">Type</Typography>
                        <Typography level="body-sm">{currentTask.task_type_display}</Typography>
                      </Box>
                      
                      <Box>
                        <Typography level="body-sm" fontWeight="md">Priority</Typography>
                        <Typography level="body-sm">{currentTask.priority_display}</Typography>
                      </Box>
                      
                      {currentTask.due_date && (
                        <Box>
                          <Typography level="body-sm" fontWeight="md">Due Date</Typography>
                          <Typography 
                            level="body-sm"
                            color={currentTask.is_overdue ? 'danger' : 'neutral'}
                          >
                            {new Date(currentTask.due_date).toLocaleDateString()}
                            {currentTask.is_overdue && ' (Overdue)'}
                          </Typography>
                        </Box>
                      )}
                      
                      {currentTask.estimated_hours && (
                        <Box>
                          <Typography level="body-sm" fontWeight="md">Estimated Hours</Typography>
                          <Typography level="body-sm">{currentTask.estimated_hours}h</Typography>
                        </Box>
                      )}
                      
                      {currentTask.assigned_to_name && (
                        <Box>
                          <Typography level="body-sm" fontWeight="md">Assigned To</Typography>
                          <Typography level="body-sm">{currentTask.assigned_to_name}</Typography>
                        </Box>
                      )}
                      
                      {currentTask.created_by_name && (
                        <Box>
                          <Typography level="body-sm" fontWeight="md">Created By</Typography>
                          <Typography level="body-sm">{currentTask.created_by_name}</Typography>
                        </Box>
                      )}
                      
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
            <TaskComments 
              taskId={currentTask.id} 
              currentUsername={currentUser?.username}
            />
          </TabPanel>

          <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
            {currentTask.instructions && currentTask.instructions.trim() !== '' ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography level="title-sm" sx={{ mb: 2 }}>
                    Task Instructions
                  </Typography>
                  <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>
                    {currentTask.instructions}
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <Typography level="body-md" sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                No instructions provided for this task
              </Typography>
            )}
          </TabPanel>
          <TabPanel value={3}><TaskWorkspace key={currentTask.id} task={currentTask} refresh={() => { refetchTask(); queryClient.invalidateQueries({ queryKey: ['taskComments', task.id] }); onUpdate(); }} /></TabPanel>
        </Tabs>
        <TaskManagementActions key={currentTask.id} task={currentTask} disabled={taskError || taskFetching} onFinished={onClose} />
      </ModalDialog>
    </Modal>

    {/* Edit Modal */}
    <TaskEditModal
      open={isEditModalOpen}
      task={currentTask}
      onClose={() => setIsEditModalOpen(false)}
      onSuccess={async (updatedTask) => {
        setIsEditModalOpen(false);
        // Refetch to ensure we have all fields
        await refetchTask();
        
        // Invalidate job tasks to update the Tasks tab
        if (currentTask.related_job) {
          queryClient.invalidateQueries({ queryKey: ['jobTasks', String(currentTask.related_job)] });
        }
        
        // Invalidate opportunity tasks if applicable
        if (currentTask.related_opportunity) {
          queryClient.invalidateQueries({ queryKey: ['opportunityTasks', String(currentTask.related_opportunity)] });
        }
        
        if (updatedTask) {
          onTaskUpdated?.(updatedTask as Task);
        }
        onUpdate();
      }}
    />
    </>
  );
};